import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, InfoIcon, LockIcon, PencilIcon, XIcon } from 'lucide-react';
import { Field } from '../types';
import { systemUsers } from '../data';
import { formatValue, initials } from '../utils/format';
import { UserLookup } from './UserLookup';
interface InlineFieldProps {
  field: Field;
  value: string | boolean;
  dirty: boolean;
  compact?: boolean;
  /** Counts toward record completion */
  required?: boolean;
  onChange: (id: string, value: string | boolean) => void;
}
export function InlineField({
  field,
  value,
  dirty,
  compact = false,
  required = false,
  onChange
}: InlineFieldProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string | boolean>(value);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null>(null);
  useEffect(() => {
    if (editing) {
      setDraft(value);
      // focus once the input mounts
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [editing, value]);
  const commit = () => {
    onChange(field.id, draft);
    setEditing(false);
  };
  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };
  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && field.type !== 'textarea') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  };
  const display = formatValue(field.type, value);
  const isEmpty = display === '';
  const canEdit = !field.readOnly;

  // ---- Checkbox renders inline (no separate edit mode) ----
  if (field.type === 'checkbox') {
    const checked = Boolean(value);
    return <FieldShell field={field} dirty={dirty} compact={compact} required={required} noEditButton data-id="element-215" data-mp="d6m7bm0k5zjc">
        <button type="button" role="checkbox" aria-checked={checked} aria-label={field.label} onClick={() => onChange(field.id, !checked)} className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-md border transition-colors ${checked ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-300 bg-white hover:border-blue-400'}`} data-id="element-216" data-mp="87htuj6lpfn7">
          {checked && <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} data-id="element-217" data-mp="eycgqexm50ui" />}
        </button>
      </FieldShell>;
  }
  return <FieldShell field={field} dirty={dirty} compact={compact} required={required} missing={required && isEmpty} canEdit={canEdit} editing={editing} onEdit={() => canEdit && setEditing(true)} data-id="element-218" data-mp="j0h8170l9ldz">
      <AnimatePresence mode="wait" initial={false} data-id="element-219" data-mp="sv9426eiw06n">
        {editing ? <motion.div key="editor" initial={{
        opacity: 0,
        y: -2
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.12
      }} className="flex items-start gap-2" data-id="element-220" data-mp="1zdfr1wvqlubl">
            {field.type === 'userLookup' ? <div className="flex-1" data-id="element-221" data-mp="3jy2onsns7cll">
                <UserLookup value={String(value ?? '')} label={field.label} onSelect={name => {
            onChange(field.id, name);
            setEditing(false);
          }} onCancel={cancel} data-id="element-222" data-mp="3f488gz7dxfr9" />
              </div> : <>
                <div className="flex-1" data-id="element-223" data-mp="2w65pb11iqd1d">
                  {renderInput(field, draft, setDraft, inputRef, handleKey)}
                </div>
                <div className="flex shrink-0 items-center gap-1 pt-0.5" data-id="element-224" data-mp="2r847ejftwjrh">
                  <button type="button" onClick={commit} aria-label="Save" className="flex h-7 w-7 items-center justify-center rounded-md bg-blue-600 text-white transition-colors hover:bg-blue-700" data-id="element-225" data-mp="ueix4w5w4c3b">
                    <CheckIcon className="h-4 w-4" data-id="element-226" data-mp="12f71v3dtdq1j" />
                  </button>
                  <button type="button" onClick={cancel} aria-label="Cancel" className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50" data-id="element-227" data-mp="12d47y89jjcrj">
                    <XIcon className="h-4 w-4" data-id="element-228" data-mp="jh4hx304ijal" />
                  </button>
                </div>
              </>}
          </motion.div> : <motion.div key="value" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.12
      }} onClick={() => canEdit && setEditing(true)} className={canEdit ? 'cursor-text' : ''} data-id="element-229" data-mp="4plu4j10vkgo">
            {renderDisplay(field, value, display, isEmpty, required)}
          </motion.div>}
      </AnimatePresence>
    </FieldShell>;
}

/* ---------- Field frame (label + hover edit affordance) ---------- */

interface FieldShellProps {
  field: Field;
  dirty: boolean;
  children: React.ReactNode;
  canEdit?: boolean;
  editing?: boolean;
  compact?: boolean;
  required?: boolean;
  missing?: boolean;
  noEditButton?: boolean;
  onEdit?: () => void;
}
function FieldShell({
  field,
  dirty,
  children,
  canEdit = true,
  editing = false,
  compact = false,
  required = false,
  missing = false,
  noEditButton = false,
  onEdit
}: FieldShellProps) {
  const frame = dirty ? 'bg-amber-50 ring-1 ring-amber-200' : missing ? 'bg-blue-50/60 ring-1 ring-blue-200 hover:bg-blue-50' : required ? 'bg-slate-50/60 hover:bg-slate-100/70' : 'hover:bg-slate-50';
  return <div className={`group relative rounded-lg transition-colors ${compact ? 'px-2.5 py-1.5' : 'px-3 py-2.5'} ${frame}`} data-id="element-230" data-mp="dvdbioerut80">
      {dirty && <span className="absolute left-0 top-2 h-[calc(100%-16px)] w-1 rounded-full bg-amber-400" data-id="element-231" data-mp="3bo5b425dzimx" />}
      {!dirty && required && <span className={`absolute left-0 top-2 h-[calc(100%-16px)] w-1 rounded-full ${missing ? 'bg-blue-500' : 'bg-slate-300'}`} aria-hidden="true" data-id="element-232" data-mp="3gm74ph0fv0cn" />}
      <div className={`flex items-center gap-1.5 ${compact ? 'mb-0.5' : 'mb-1'}`} data-id="element-233" data-mp="3qc9wjdvtq89e">
        <span className={`text-[11px] font-bold uppercase tracking-wide ${required ? 'text-slate-700' : 'text-slate-500'}`} data-id="element-234" data-mp="3vb3af7f0w1a3">
          {field.label}
        </span>
        {required && <span className={`text-xs font-bold leading-none ${missing ? 'text-blue-600' : 'text-slate-400'}`} aria-label="Required for record completion" title="Required for record completion" data-id="element-235" data-mp="86nfct2w222v">
            *
          </span>}
        {field.hint && <span className="group/hint relative inline-flex" data-id="element-236" data-mp="1xok8mm8x0s95">
            <InfoIcon className="h-3 w-3 text-slate-400" data-id="element-237" data-mp="1xn976t2xzwl7" />
            <span className="pointer-events-none absolute left-1/2 top-5 z-20 w-52 -translate-x-1/2 rounded-md bg-slate-800 px-2.5 py-1.5 text-[11px] font-normal normal-case leading-snug text-white opacity-0 shadow-lg transition-opacity group-hover/hint:opacity-100" data-id="element-238" data-mp="1sopl8socmksu">
              {field.hint}
            </span>
          </span>}
        {field.readOnly && <LockIcon className="h-3 w-3 text-slate-400" data-id="element-239" data-mp="2gd8sd4vnwrjc" />}
        {dirty ? <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide text-amber-600" data-id="element-240" data-mp="2bee2jiza22pq">
            Edited
          </span> : missing && <span className="ml-auto rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-blue-700" data-id="element-241" data-mp="101uzlsfspfds">
              Required
            </span>}
      </div>

      {children}

      {!noEditButton && canEdit && !editing && <button type="button" onClick={onEdit} aria-label={`Edit ${field.label}`} className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-md text-slate-400 opacity-0 transition-all hover:bg-white hover:text-blue-600 hover:shadow-sm group-hover:opacity-100 focus:opacity-100" data-id="element-242" data-mp="303kue74gt7me">
          <PencilIcon className="h-3.5 w-3.5" data-id="element-243" data-mp="3esr0mf1pzy3s" />
        </button>}
    </div>;
}

/* ---------- Display renderers ---------- */

function renderDisplay(field: Field, value: string | boolean, display: string, isEmpty: boolean, required = false) {
  if (isEmpty) {
    if (required) {
      return <span className="text-sm font-semibold text-blue-600" data-id="element-244" data-mp="39ywdt2qtj30h">
          {field.type === 'userLookup' ? 'Needs an assignment' : 'Needs a value'}
        </span>;
    }
    return <span className="text-sm italic text-slate-300" data-id="element-245" data-mp="6e8qm7s7y8hj">
        {field.type === 'userLookup' ? 'Unassigned' : 'Empty'}
      </span>;
  }
  if (field.type === 'user') {
    const name = String(value);
    return <span className="inline-flex items-center gap-2" data-id="element-246" data-mp="pgra7ogtgwcb">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-[10px] font-semibold text-blue-700" data-id="element-247" data-mp="pg98wdfarrxj">
          {initials(name)}
        </span>
        <span className="text-sm font-medium text-blue-700 hover:underline" data-id="element-248" data-mp="3syiyosvquy5r">{name}</span>
      </span>;
  }
  if (field.type === 'userLookup') {
    const name = String(value);
    const user = systemUsers.find(u => u.name === name);
    return <span className="inline-flex min-w-0 items-center gap-2" data-id="element-249" data-mp="6nwwsyyudca7">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-600" data-id="element-250" data-mp="1pllejswetpv">
          {initials(name)}
        </span>
        <span className="min-w-0" data-id="element-251" data-mp="1d5a9xflsw0ss">
          <span className="block truncate text-sm font-medium text-slate-800" data-id="element-252" data-mp="1d8fymtqip0nz">{name}</span>
          {user && <span className="block truncate text-[11px] text-slate-400" data-id="element-253" data-mp="1g4nxltfrviie">{user.role}</span>}
        </span>
      </span>;
  }
  if (field.type === 'select') {
    return <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-sm font-medium text-slate-700" data-id="element-254" data-mp="16axdt1ulbw91">
        {display}
      </span>;
  }
  if (field.type === 'currency' || field.type === 'percent') {
    return <span className="text-sm font-semibold tabular-nums text-slate-900" data-id="element-255" data-mp="sdim1qgv7cdw">{display}</span>;
  }
  return <span className="text-sm text-slate-800" data-id="element-256" data-mp="ndw9lj8v2065">{display}</span>;
}

/* ---------- Editors ---------- */

function renderInput(field: Field, draft: string | boolean, setDraft: (v: string | boolean) => void, ref: React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null>, onKeyDown: (e: React.KeyboardEvent) => void) {
  const base = 'w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 shadow-sm outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-100';
  if (field.type === 'textarea') {
    return <textarea ref={el => ref.current = el} value={String(draft)} onChange={e => setDraft(e.target.value)} onKeyDown={onKeyDown} rows={3} className={`${base} resize-none`} data-id="element-257" data-mp="2u24wksi9bbwf" />;
  }
  if (field.type === 'select') {
    return <div className="relative" data-id="element-258" data-mp="26c26ghxwkg0g">
        <select ref={el => ref.current = el} value={String(draft)} onChange={e => setDraft(e.target.value)} onKeyDown={onKeyDown} className={`${base} appearance-none pr-8`} data-id="element-259" data-mp="26dc6hcobfy9q">
          <option value="" data-id="element-260" data-mp="3mr2dar6tl0n2">— Select —</option>
          {field.options?.map(opt => <option key={opt} value={opt} data-id="element-261" data-mp="3mtg4uq2ajupv">
              {opt}
            </option>)}
        </select>
        <ChevronDownIcon className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" data-id="element-262" data-mp="32219aqz2vu2s" />
      </div>;
  }
  if (field.type === 'date') {
    return <input ref={el => ref.current = el} type="date" value={String(draft)} onChange={e => setDraft(e.target.value)} onKeyDown={onKeyDown} className={base} data-id="element-263" data-mp="3c0764u612a0s" />;
  }
  const inputMode = field.type === 'currency' || field.type === 'percent' || field.type === 'number' ? 'decimal' : 'text';
  return <div className="relative" data-id="element-264" data-mp="2eac9kpjxg3bt">
      {field.type === 'currency' && <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400" data-id="element-265" data-mp="2t24nqy7zjbby">
          $
        </span>}
      <input ref={el => ref.current = el} type="text" inputMode={inputMode} value={String(draft)} onChange={e => setDraft(e.target.value)} onKeyDown={onKeyDown} className={`${base} ${field.type === 'currency' ? 'pl-6' : ''} ${field.type === 'percent' ? 'pr-6' : ''}`} data-id="element-266" data-mp="20l21hkmc61ej" />
      {field.type === 'percent' && <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400" data-id="element-267" data-mp="15qrrrudymfy">
          %
        </span>}
    </div>;
}